import { useMemo } from "react";
import { Clock, ChevronsRight, CheckCircle2, XCircle, Stamp, Eye, Bell } from "lucide-react";
import { subtypePalette } from "@/lib/aviSubtypePalette";

/**
 * AviKanbanView — Kanban 4 colonnes (§rev-avi-5, Feb 2026).
 *
 * Colonnes :
 *   1. En attente          (aucune réponse encore posée)
 *   2. Partiellement       (au moins un décideur a répondu, pas encore figé)
 *   3. Terminal positif    (Approuvée / Reçue / Lue, selon sous-type)
 *   4. Refusée / Annulée   (statut final rouge + gris)
 *
 * Spec iter164l (2026-02-25) — Colonnes 1/2 et carte cadre héritent
 * de la palette du sous-type (Validation bleu / Accusé orange /
 * Information vert). La colonne 3 « positif » utilise la
 * `positiveColor` de la palette (vert pour Validation, orange pour
 * Accusé, vert-marque pour Information). La colonne « Refusée /
 * Annulée » reste rouge — sémantique universelle préservée.
 *
 * Aucune action drag-and-drop : les mouvements se font via les modales
 * Approuver/Refuser/Annuler de la fiche (spec §4).
 */
function buildCols(subtype) {
  const p = subtypePalette(subtype);
  const isAccuse = subtype === "accuse";
  const isInfo = subtype === "information";
  // Spec iter164R (2026-02-27) — Information : Kanban simplifié à 2
  // colonnes (Non lu / Lu). Les statuts "partiel_repondu" et
  // "refusee/annulee" n'ont pas de sens pour une simple information
  // à consulter.
  if (isInfo) {
    return [
      {
        key: "non_lu",
        label: "Non lu",
        color: p.kanbanWaitingColor,
        Icon: Eye,
        matches: (s) => s !== "lue",   // tout ce qui n'est pas encore lu
      },
      {
        key: "lue",
        label: "Lu",
        color: p.positiveColor,
        Icon: Eye,
        matches: (s) => s === "lue",
      },
    ];
  }
  const posLabel = isAccuse ? "Reçu" : "Approuvée";
  const posIcon = isAccuse ? Stamp : CheckCircle2;
  const posKey = isAccuse ? "accusee" : "approuvee";
  return [
    { key: "en_attente",      label: "En attente",        color: p.kanbanWaitingColor, Icon: Clock,        matches: (s) => s === "en_attente" },
    { key: "partiel_repondu", label: "Partiellement",     color: p.kanbanPartialColor, Icon: ChevronsRight, matches: (s) => s === "partiel_repondu" },
    { key: posKey,            label: posLabel,            color: p.positiveColor,      Icon: posIcon,       matches: (s) => s === posKey },
    { key: "refusee",         label: "Refusée / Annulée", color: "#F87171",            Icon: XCircle,       matches: (s) => s === "refusee" || s === "annulee" },
  ];
}

const PRIO_DOT = {
  immediat: "#EF4444", urgent: "#F97316", moyen: "#F9BC26", normal: "#4ADE80",
};

function Card({ item, currentUserId, onOpen, cols }) {
  const color = cols.find((c) => c.matches(item.status))?.color || "#9CA3AF";
  const isCancelled = item.status === "annulee";
  const isMine = item.created_by === currentUserId;
  const deciders = (item.participants || []).filter((p) => p.role === "decideur");
  const answered = deciders.filter((p) => p.decision).length;
  const total = deciders.length;
  const unread = item.messages_unread || 0;
  return (
    <button
      type="button"
      onClick={() => onOpen(item)}
      className="w-full rounded-lg p-3 text-left transition-all hover:brightness-110"
      style={{
        background: "#161616",
        border: `1px solid ${isCancelled ? "#3A3A3F" : color + "44"}`,
        boxShadow: `inset 3px 0 0 0 ${isCancelled ? "#6B7280" : color}`,
        opacity: isCancelled ? 0.72 : 1,
      }}
      data-testid={`avi-kanban-card-${item.id}`}
    >
      <div className="flex items-center gap-2 mb-1.5">
        <span className="font-mono text-[10.5px]" style={{ color }}>{item.reference}</span>
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: PRIO_DOT[item.priority] }}
              title={item.priority}></span>
        {/* Spec iter199 (2026-02-28) — Cloche 🔔 sur les cartes
            Kanban dont le rappel automatique va se déclencher dans
            les 24h à venir. Cohérent avec le KPI + hovercard hopper.
            Spec iter200 (2026-02-28) — pulsation douce quand
            l'échéance est < 1h (halo jaune expand/fade en boucle). */}
        {item._reminder_soon && (
          <span
            className={`inline-flex items-center justify-center rounded-full flex-shrink-0${item._reminder_urgent ? " avi-bell-pulse" : ""}`}
            style={{
              width: 15, height: 15,
              background: "#F5C84222",
              border: "1px solid #F5C84266",
            }}
            title={item._reminder_due_at
              ? `Rappel prévu ${new Date(item._reminder_due_at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}${item._reminder_urgent ? " (< 1h)" : ""}`
              : "Rappel < 24h"}
            data-testid={`avi-kanban-bell-${item.id}`}
          >
            <Bell size={8.5} strokeWidth={2.8} style={{ color: "#F5C842" }} />
          </span>
        )}
        {isCancelled && (
          <span className="text-[9px] uppercase tracking-widest px-1.5 rounded"
                style={{ background: "#3A3A3F", color: "#9CA3AF" }}>Annulée</span>
        )}
        {isMine && (
          <span className="ml-auto text-[9px] uppercase tracking-widest text-muted-foreground">Moi</span>
        )}
      </div>
      <div className="text-[13px] font-semibold leading-snug truncate" title={item.subject}>
        {item.subject}
      </div>
      <div className="flex items-center justify-between gap-2 mt-2 text-[10.5px] text-muted-foreground">
        <span>{answered}/{total} décideur{total > 1 ? "s" : ""} · {new Date(item.created_at).toLocaleDateString("fr-FR")}</span>
        {unread > 0 && (
          <span
            className="inline-flex items-center gap-1 h-[18px] px-1.5 rounded-full text-[9.5px] font-bold text-white flex-shrink-0"
            style={{ background: "#3B82F6", boxShadow: "0 2px 6px rgba(59,130,246,0.4)" }}
            data-testid={`avi-kanban-unread-${item.id}`}
            title={`${unread} nouveau(x) message(s)`}
          >
            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M4 4h16v12H8l-4 4V4z" fill="#FFFFFF" />
            </svg>
            +{unread} nouveaux
          </span>
        )}
      </div>
    </button>
  );
}

export default function AviKanbanView({ items = [], loading = false, currentUserId, subtype = "validation", onOpen }) {
  const cols = useMemo(() => buildCols(subtype), [subtype]);
  const grouped = useMemo(() => {
    const g = {};
    for (const c of cols) g[c.key] = [];
    for (const it of items) {
      const col = cols.find((c) => c.matches(it.status));
      if (col) g[col.key].push(it);
    }
    return g;
  }, [items, cols]);

  return (
    <div className={`grid ${cols.length === 2 ? "grid-cols-2" : "grid-cols-4"} gap-3`} data-testid="avi-kanban-view">
      {cols.map((col) => {
        const list = grouped[col.key] || [];
        const Icon = col.Icon;
        return (
          <div
            key={col.key}
            className="rounded-xl flex flex-col"
            style={{
              background: "#141416",
              border: "1px solid #2A2A2A",
              minHeight: 400,
              maxHeight: "calc(100vh - 360px)",
            }}
            data-testid={`avi-kanban-col-${col.key}`}
          >
            <div className="flex items-center gap-2 px-3 py-2.5 rounded-t-xl"
                 style={{ background: `${col.color}12`, borderBottom: `1px solid ${col.color}33` }}>
              {/* Spec iter164n (2026-02-25) — Pastille 8px de la couleur
                  du statut devant l'icône et le titre. Renforce le
                  pattern « pastille = statut = couleur » déjà en place
                  sur les badges, hovercard et tuiles KPI. */}
              <span
                aria-hidden="true"
                className="inline-block rounded-full flex-shrink-0"
                style={{
                  width: 8, height: 8,
                  background: col.color,
                  boxShadow: `0 0 0 2px ${col.color}22`,
                }}
              />
              <Icon size={13} style={{ color: col.color }} />
              <span className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: col.color }}>
                {col.label}
              </span>
              <span className="ml-auto text-[11px] font-bold" style={{ color: col.color }}>{list.length}</span>
            </div>
            <div className="p-2 space-y-2 overflow-y-auto flex-1">
              {loading && list.length === 0 && (
                <div className="text-center text-[11px] text-muted-foreground py-6">Chargement…</div>
              )}
              {!loading && list.length === 0 && (
                <div className="text-center text-[11px] text-muted-foreground py-6"
                     data-testid={`avi-kanban-empty-${col.key}`}>
                  Aucune demande.
                </div>
              )}
              {list.map((it) => (
                <Card key={it.id} item={it} currentUserId={currentUserId} onOpen={onOpen} cols={cols} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
